import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { ArrowLeftIcon, ArrowPathIcon, DevicePhoneMobileIcon, DocumentIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { Alert, Button, Card, PageHeader } from '../components/ui';
import onboarding from '../services/accountOnboardingService';
import { codeDeliveryHint, encodeSession, onboardingErrorMessage, onboardingState, safeQrImage, sessionFiles, sessionMetadata } from '../utils/accountOnboarding';

const METHODS = [
  { id: 'phone', label: 'Телефон', icon: DevicePhoneMobileIcon },
  { id: 'qr', label: 'QR-код', icon: QrCodeIcon },
  { id: 'session', label: 'Файлы сессии', icon: DocumentIcon },
];
const ROLES = { monitoring: 'Observer — мониторинг', agent: 'Agent — работа в Messenger', ai: 'AI — ИИ-персонаж' };
const inputClass = 'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500';

export default function AccountNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [recommended] = useState(() => window.matchMedia('(max-width: 900px)').matches ? 'phone' : 'qr');
  const [method, setMethod] = useState(recommended);
  const [type, setType] = useState('monitoring');
  const [phase, setPhase] = useState('setup');
  const [phone, setPhone] = useState('');
  const [files, setFiles] = useState(null);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [profile, setProfile] = useState({ first_name: '', last_name: '', bio: '' });
  const [leaveGroups, setLeaveGroups] = useState(false);
  const [flowId, setFlowId] = useState('');
  const [details, setDetails] = useState({});
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInput = useRef(null);
  const active = useRef(true);
  const currentFlow = useRef('');
  const completed = useRef(false);
  const requestPending = useRef(false);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      if (currentFlow.current && !completed.current) onboarding.cancel(currentFlow.current).catch(() => {});
    };
  }, []);

  const clearFiles = () => {
    setFiles(null);
    setSessionClosed(false);
    if (fileInput.current) fileInput.current.value = '';
  };

  const applyFlow = useCallback((flow) => {
    const next = onboardingState(flow);
    setPhase(next);
    setDetails({
      qr_image: next === 'qr' ? safeQrImage(flow?.qr_image) : null,
      code_delivery: flow?.code_delivery,
      phone_hint: flow?.phone_hint,
      account_id: flow?.account_id,
    });
    if (next === 'ready') {
      completed.current = true;
      queryClient.invalidateQueries('accounts');
    }
    if (next === 'failed') setError(flow?.error?.message || (typeof flow?.error === 'string' ? flow.error : '') || 'Telegram не завершил подключение.');
  }, [queryClient]);

  useEffect(() => {
    if (cancelling || !flowId || !['connecting', 'qr'].includes(phase)) return undefined;
    let polling = true;
    let timer;
    const poll = async () => {
      try {
        const flow = await onboarding.get(flowId);
        if (polling) applyFlow(flow);
      } catch (pollError) {
        if (polling) { setError(onboardingErrorMessage(pollError)); setPhase('failed'); }
      } finally {
        if (polling) timer = window.setTimeout(poll, 1800);
      }
    };
    poll();
    return () => { polling = false; window.clearTimeout(timer); };
  }, [applyFlow, cancelling, flowId, phase]);

  const acceptFiles = (selected) => {
    const list = Array.from(selected || []);
    clearFiles();
    setError('');
    try { setFiles(sessionFiles(list)); } catch (fileError) { setError(fileError.message); }
  };

  const start = async (event) => {
    event.preventDefault();
    if (requestPending.current || (method === 'phone' && !phone.trim()) || (method === 'session' && (!files || !sessionClosed))) return;
    requestPending.current = true;
    setBusy(true);
    setError('');
    try {
      const body = { auth_method: method, type, leave_existing_groups: type === 'agent' && leaveGroups };
      if (method === 'phone') body.phone = phone.trim();
      if (method === 'session') {
        try { body.package = sessionMetadata(JSON.parse(await files.metadata.text())); }
        catch (_error) { throw new Error('Не удалось прочитать JSON аккаунта. Выберите исходный файл.'); }
        body.session_base64 = await encodeSession(files.session);
        body.session_confirmed_closed = sessionClosed;
      }
      Object.entries(profile).forEach(([key, value]) => { if (value.trim()) body[key] = value.trim(); });
      if (!active.current) return;
      const flow = await onboarding.start(body);
      const id = String(flow?.flow_id || flow?.id || '');
      if (!active.current) {
        if (id) onboarding.cancel(id).catch(() => {});
        return;
      }
      if (!id) throw new Error('Сервер не создал процесс подключения.');
      currentFlow.current = id;
      setFlowId(id);
      setPhone('');
      applyFlow(flow);
    } catch (startError) {
      if (active.current) setError(onboardingErrorMessage(startError));
    } finally {
      requestPending.current = false;
      if (active.current) { clearFiles(); setBusy(false); }
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (requestPending.current) return;
    requestPending.current = true;
    setBusy(true);
    setError('');
    try {
      const flow = phase === 'code'
        ? await onboarding.code(flowId, code.replace(/\s/g, ''))
        : await onboarding.password(flowId, password);
      if (active.current) applyFlow(flow);
    } catch (submitError) {
      if (active.current) setError(onboardingErrorMessage(submitError));
    } finally {
      requestPending.current = false;
      if (active.current) { setCode(''); setPassword(''); setBusy(false); }
    }
  };

  const close = async () => {
    if (requestPending.current) return;
    requestPending.current = true;
    setBusy(true);
    setError('');
    setCancelling(true);
    try {
      if (currentFlow.current && !completed.current) await onboarding.cancel(currentFlow.current);
    } catch (cancelError) {
      if (!['AUTH_SESSION_EXPIRED', 'AGENT_FLOW_NOT_FOUND'].includes(cancelError.response?.data?.code)) {
        requestPending.current = false;
        if (active.current) { setError(onboardingErrorMessage(cancelError)); setBusy(false); }
        return;
      }
    }
    currentFlow.current = '';
    if (active.current) { clearFiles(); setCode(''); setPassword(''); navigate('/accounts'); }
  };

  const step = ({ setup: 0, code: 1, qr: 1, password: 2, connecting: 3, ready: 3, failed: 3 })[phase];
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Добавить аккаунт" description="Подключение Telegram, как в LeadPulse Messenger" actions={<Button variant="secondary" icon={ArrowLeftIcon} disabled={busy} onClick={close}>К аккаунтам</Button>} />
      <Card>
        <ol className="mb-6 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4" aria-label="Шаги подключения">
          {['Способ входа', 'Подтверждение', 'Пароль, если нужен', 'Готово'].map((label, index) => <li key={label} aria-current={step === index ? 'step' : undefined} className={`rounded-md px-3 py-2 ${step === index ? 'bg-primary-50 text-primary-700 font-medium' : 'bg-gray-50 text-gray-500'}`}>{index + 1}. {label}</li>)}
        </ol>
        {error && <div role="alert" className="mb-4"><Alert variant="error">{error}</Alert></div>}

        {phase === 'setup' && <form onSubmit={start} className="space-y-5">
          <fieldset disabled={busy} className="space-y-5">
            <label className="block text-sm font-medium text-gray-700">Тип аккаунта
              <select aria-label="Тип аккаунта" className={inputClass} value={type} onChange={event => { setType(event.target.value); setLeaveGroups(false); }}>
                {Object.entries(ROLES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            {type === 'ai' && <p className="text-sm text-gray-500">Отдельный тип для ИИ-персонажей. Аккаунт не участвует в мониторинге и работе сотрудников Messenger.</p>}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="group" aria-label="Способ входа в Telegram">
              {METHODS.map(({ id, label, icon: Icon }) => <button key={id} type="button" aria-pressed={method === id} onClick={() => { setMethod(id); clearFiles(); setPhone(''); setError(''); }} className={`rounded-lg border p-4 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 ${method === id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <Icon className="mb-2 h-6 w-6" /><span className="block font-medium">{label}</span><span className="text-xs text-gray-500">{recommended === id ? 'Рекомендуем' : id === 'session' ? '.session + .json' : id === 'qr' ? 'С другого устройства' : 'По номеру'}</span>
              </button>)}
            </div>
            {method === 'phone' && <label className="block text-sm font-medium text-gray-700">Номер телефона Telegram<input type="tel" autoComplete="tel" inputMode="tel" required value={phone} onChange={event => setPhone(event.target.value)} placeholder="+7 999 123-45-67" className={inputClass} /><span className="mt-1 block text-xs text-gray-500">С кодом страны. Файлы для входа не нужны.</span></label>}
            {method === 'qr' && <p className="text-sm text-gray-600">Откройте Telegram на телефоне: Настройки → Устройства → Подключить устройство. Нажмите «Показать QR-код» ниже.</p>}
            {method === 'session' && <div className="space-y-3">
              <p className="text-sm text-gray-600">Выберите .session и .json одного аккаунта. Папка tdata не подходит.</p>
              <label className={`block cursor-pointer rounded-lg border-2 border-dashed p-5 ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`}
                onDragOver={event => { event.preventDefault(); if (!busy) setDragActive(true); }} onDragLeave={() => setDragActive(false)}
                onDrop={event => { event.preventDefault(); setDragActive(false); if (!busy) acceptFiles(event.dataTransfer?.files); }}>
                <span className="mb-2 block break-all text-sm">{files ? `${files.session.name} + ${files.metadata.name}` : 'Выберите или перетащите два файла'}</span>
                <input ref={fileInput} type="file" multiple accept=".session,.json,application/json" aria-label="Файлы сессии Telegram" onChange={event => acceptFiles(event.target.files)} className="block w-full min-w-0 text-sm" />
                <span className="mt-2 block text-xs text-gray-500">.session до 2 МБ · .json до 64 КБ</span>
              </label>
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={sessionClosed} onChange={event => setSessionClosed(event.target.checked)} /><span>Исходная сессия закрыта на других устройствах.<span className="block text-xs text-gray-500">Остановите программы с этими файлами до завершения импорта.</span></span></label>
            </div>}
            <details className="rounded-lg border border-gray-200 p-3"><summary className="cursor-pointer text-sm text-gray-600">Изменить профиль · необязательно</summary>
              <div className="mt-3 space-y-3">{[['first_name', 'Имя', 64], ['last_name', 'Фамилия', 64], ['bio', 'О себе', 255]].map(([key, label, length]) => <label key={key} className="block text-sm text-gray-700">{label}<input maxLength={length} value={profile[key]} onChange={event => setProfile(current => ({ ...current, [key]: event.target.value.replace(/[\r\n]/g, ' ') }))} placeholder="Оставить текущее" className={inputClass} /></label>)}</div>
            </details>
            {type === 'agent' && <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={leaveGroups} onChange={event => setLeaveGroups(event.target.checked)} /><span>Отписаться от существующих групп и каналов<span className="block text-xs text-gray-500">Необязательно. По умолчанию подписки сохраняются.</span></span></label>}
          </fieldset>
          <div className="flex flex-wrap justify-end gap-2"><Button variant="secondary" disabled={busy} onClick={close}>Отмена</Button><Button type="submit" isLoading={busy} disabled={(method === 'phone' && !phone.trim()) || (method === 'session' && (!files || !sessionClosed))}>{method === 'qr' ? 'Показать QR-код' : method === 'session' ? 'Подключить аккаунт' : 'Получить код'}</Button></div>
        </form>}

        {phase === 'qr' && <div className="space-y-4 text-center"><h2 className="text-lg font-medium">Сканируйте код в Telegram</h2>{details.qr_image ? <img src={details.qr_image} width="240" height="240" className="mx-auto max-w-full" alt="QR-код для подключения Telegram" /> : <p role="status">Обновляем QR-код…</p>}<p className="text-sm text-gray-600">На телефоне: Настройки → Устройства → Подключить устройство.</p><p className="text-xs text-gray-500">QR-код обновляется автоматически.</p></div>}
        {['code', 'password'].includes(phase) && <form onSubmit={submit} className="space-y-4">
          <h2 className="text-lg font-medium">{phase === 'code' ? 'Введите код подтверждения' : 'Введите пароль Telegram'}</h2>
          <p className="text-sm text-gray-600">{phase === 'code' ? codeDeliveryHint(details.code_delivery) : 'Облачный пароль Telegram-аккаунта. Он используется только для подтверждения этого входа.'}</p>
          {phase === 'code' && details.phone_hint && <p className="text-sm text-gray-500">{details.phone_hint}</p>}
          {phase === 'code' ? <label className="block text-sm font-medium">Код Telegram<input autoFocus inputMode="numeric" autoComplete="one-time-code" disabled={busy || cancelling} value={code} onChange={event => setCode(event.target.value.replace(/[^0-9\s]/g, ''))} className={inputClass} /></label> : <label className="block text-sm font-medium">Пароль двухэтапной аутентификации<input autoFocus type="password" autoComplete="current-password" disabled={busy || cancelling} value={password} onChange={event => setPassword(event.target.value)} className={inputClass} /></label>}
          <Button type="submit" isLoading={busy} disabled={cancelling || (phase === 'code' ? code.replace(/\s/g, '').length < 4 : !password)}>Подтвердить и продолжить</Button>
        </form>}
        {phase === 'connecting' && <div className="space-y-3 py-6 text-center" role="status"><ArrowPathIcon className="mx-auto h-8 w-8 animate-spin text-primary-600" /><h2 className="text-lg font-medium">Подключаем аккаунт</h2><p className="text-sm text-gray-600">Проверяем Telegram-сессию и синхронизируем профиль.</p></div>}
        {phase === 'ready' && <div className="space-y-4"><Alert variant="success" title="Аккаунт подключён">Тип: {ROLES[type]}. Telegram подтвердил вход.</Alert><Button onClick={() => navigate(details.account_id ? `/accounts/${encodeURIComponent(details.account_id)}` : '/accounts')}>Открыть аккаунт</Button></div>}
        {phase === 'failed' && <h2 className="text-lg font-medium">Подключение не завершено</h2>}
        {!['setup', 'ready'].includes(phase) && <div className="mt-5"><Button variant="secondary" disabled={busy} onClick={close}>{cancelling ? 'Повторить отмену' : 'Отменить подключение'}</Button></div>}
        {cancelling && busy && <p className="mt-3 text-sm text-gray-500" role="status">Останавливаем подключение…</p>}
      </Card>
      {phase === 'setup' && <div className="mt-4 text-center"><Button variant="secondary" to={busy ? undefined : '/accounts/new/manual'} disabled={busy}>Расширенная форма с API ID и API Hash</Button></div>}
    </div>
  );
}
